import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { UserProfile, UserCredits, SubscriptionDetails } from '@/domain/entities/UserProfile'
import type { MemberProfileDetails, UpdateMemberProfilePayload } from '@/domain/entities/MemberProfile'
import { UserProfileService } from '@/features/user/services/user-profile.service'

export const useUserStore = defineStore('user', () => {
  const service = new UserProfileService()

  const profile = ref<UserProfile | null>(null)
  const credits = ref<UserCredits | null>(null)
  const subscription = ref<SubscriptionDetails | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  // Credits fetch tracked separately from `error` (which is the profile fetch): a credits-only
  // failure must surface its own retry in the dashboard credit tiles, not the generic banner.
  const creditsError = ref(false)

  // Member profile (Profile page) state — NOT persisted (contains PII)
  const memberProfile = ref<MemberProfileDetails | null>(null)
  const memberProfileLoading = ref(false)
  const memberProfileSaving = ref(false)
  const memberProfileError = ref<string | null>(null)

  // Deduplication guard + generation counter for $reset race protection
  let fetchPromise: Promise<void> | null = null
  let generation = 0

  // API returns PascalCase (backend uses Newtonsoft DefaultContractResolver)
  const isAdmin = computed(() => profile.value?.IsAppAdmin ?? false)
  const isSalesUser = computed(() => profile.value?.IsSalesUser ?? false)
  const userEmail = computed(() => profile.value?.Email ?? '')
  const isOrganicFreeUser = computed(() => profile.value?.IsOrganicFreeUser ?? false)
  const isImpersonated = computed(() => profile.value?.Impersonated === true)
  const hasInfiniteSearches = computed(() => {
    const search = credits.value?.availableSearchCredit ?? 0
    return search === -1 || search > 100_000
  })

  async function fetchProfile(): Promise<void> {
    const gen = generation
    try {
      const result = await service.getUserProfile()
      if (gen === generation) profile.value = result
    } catch (e) {
      if (gen === generation) error.value = (e as Error).message
    }
  }

  async function fetchCredits(): Promise<void> {
    const gen = generation
    if (gen === generation) creditsError.value = false
    try {
      const result = await service.getCredits()
      if (gen === generation) {
        credits.value = result
        creditsError.value = false
      }
    } catch (e) {
      if (gen === generation) {
        creditsError.value = true
        console.warn('Failed to fetch credits:', (e as Error).message)
      }
    }
  }

  async function fetchSubscription(): Promise<void> {
    const gen = generation
    try {
      const result = await service.getSubscriptionDetails()
      if (gen === generation) subscription.value = result
    } catch (e) {
      if (gen === generation) console.warn('Failed to fetch subscription:', (e as Error).message)
    }
  }

  async function fetchAll(): Promise<void> {
    if (fetchPromise) return fetchPromise
    const gen = generation
    loading.value = true
    error.value = null
    creditsError.value = false
    fetchPromise = Promise.all([
      fetchProfile(),
      fetchCredits(),
      fetchSubscription(),
    ]).then(() => {}).finally(() => {
      if (gen === generation) {
        loading.value = false
        fetchPromise = null
      }
    })
    return fetchPromise
  }

  async function fetchMemberProfile(): Promise<void> {
    const gen = generation
    memberProfileLoading.value = true
    memberProfileError.value = null
    try {
      const result = await service.getMemberProfileDetails()
      if (gen === generation) memberProfile.value = result
    } catch (e) {
      if (gen === generation) memberProfileError.value = (e as Error).message
    } finally {
      if (gen === generation) memberProfileLoading.value = false
    }
  }

  async function updateMemberProfile(payload: UpdateMemberProfilePayload): Promise<void> {
    // Hard guard against synchronous double-click (button disable may lag one reactivity tick)
    if (memberProfileSaving.value) return

    memberProfileSaving.value = true
    memberProfileError.value = null
    try {
      // Phase 1: actual save — errors from this phase are user-facing
      try {
        await service.updateMemberProfile(payload)
      } catch (e) {
        memberProfileError.value = (e as Error).message
        throw e // re-throw so composable shows error toast
      }

      // Phase 2: optimistic local update (fast UI feedback even if re-fetch lags)
      if (memberProfile.value) {
        memberProfile.value = {
          ...memberProfile.value,
          Profile: {
            ...memberProfile.value.Profile,
            PhoneNumber: payload.PhoneNumber,
            CountryId: payload.CountryId,
          },
        }
      }

      // Phase 3: best-effort re-fetch — failures here are silent (save already succeeded)
      try {
        await fetchMemberProfile()
      } catch {
        // swallow — memberProfile already has optimistic update
      }
      // fetchMemberProfile may have set memberProfileError on a transient GET failure;
      // save itself succeeded so clear it.
      memberProfileError.value = null
    } finally {
      memberProfileSaving.value = false
    }
  }

  function $reset() {
    generation++
    profile.value = null
    credits.value = null
    subscription.value = null
    loading.value = false
    error.value = null
    creditsError.value = false
    memberProfile.value = null
    memberProfileLoading.value = false
    memberProfileSaving.value = false
    memberProfileError.value = null
    fetchPromise = null
  }

  return {
    profile, credits, subscription, loading, error, creditsError,
    memberProfile, memberProfileLoading, memberProfileSaving, memberProfileError,
    isAdmin, isSalesUser, userEmail, isOrganicFreeUser, isImpersonated, hasInfiniteSearches,
    fetchProfile, fetchCredits, fetchSubscription, fetchAll,
    fetchMemberProfile, updateMemberProfile,
    $reset,
  }
}, {
  persist: {
    pick: ['profile'],
  },
})
